import { pluralRu } from '@wit-support/ui';
import { afterFees, intRu, num2, pct } from './format.js';
import { FACT_MIN_N, FACT_READY_SHARE, FORECAST_HORIZONS, factPoint, factTooFew, firstPendingFactFrom, horizonHasMatureFact, } from './fact-maturity.js';
import { addDays, dayLabel } from './period.js';
import { countAcc, countOf, countWord, stepWords, v, vn } from './step-words.js';
import { sortBy } from './table-sort.js';
import { FEW_PURCHASES, rowLabel, shortRowLabel } from './trend-logic.js';
export { FACT_MIN_N, FACT_READY_SHARE, FEW_PURCHASES, factPoint, factTooFew, };
export const STAGES = ['t0', 't24', 'd14'];
export const STAGE_LABEL = {
    t0: 'при покупке',
    t24: 'через сутки',
    d14: 'на 14-й день',
};
/** Этап посчитан для корзины, если прогноз есть у ≥ 90 % её покупок. */
export const STAGE_READY_SHARE = 0.9;
/** Через сколько дней после покупки появляется прогноз этапа. */
export const STAGE_DELAY_DAYS = { t0: 0, t24: 1, d14: 14 };
export const MOMENTS_VIEWS = ['ladder', 'map', 'accuracy'];
export const DEFAULT_MOMENTS_VIEW = 'ladder';
/** Срок блока по умолчанию — 120 дней, как у вывода раздела. */
export const DEFAULT_MOMENTS_HORIZON = '120';
export const LADDER_SORTS = ['week', 'shift', 'n'];
export function readMomentsView(v) {
    return MOMENTS_VIEWS.includes(v) ? v : DEFAULT_MOMENTS_VIEW;
}
export function readMomentsHorizon(v) {
    return FORECAST_HORIZONS.includes(v)
        ? v
        : DEFAULT_MOMENTS_HORIZON;
}
export function readLadderSort(v) {
    return LADDER_SORTS.includes(v) ? v : 'week';
}
const ok = (x) => typeof x === 'number' && Number.isFinite(x);
/** Доля `a / b − 1`; без базы — `null`. */
export function relDelta(a, b) {
    return ok(a) && ok(b) && b !== 0 ? a / b - 1 : null;
}
function stagePoint(row, stage, h) {
    const g = row.group;
    const coverage = ok(g.coverage)
        ? g.coverage
        : g.purchases > 0
            ? g.purchasesWithPrediction / g.purchases
            : null;
    const af = afterFees(g.horizons[h]);
    return {
        stage,
        ready: coverage !== null && coverage >= STAGE_READY_SHARE,
        purchases: g.purchases,
        withPrediction: g.purchasesWithPrediction,
        coverage,
        readyFrom: addDays(row.to, STAGE_DELAY_DAYS[stage]),
        roas: af.available ? af.roas : null,
        lo: af.available ? af.lo : null,
        hi: af.available ? af.hi : null,
        p: af.available ? af.p : null,
        predMatured: g.factHorizons[h]?.predRoasMatured ?? null,
    };
}
function crossOf(prev, next, theta) {
    if (!prev?.ready || !next?.ready || !ok(prev.roas) || !ok(next.roas))
        return null;
    const a = prev.roas >= theta;
    const b = next.roas >= theta;
    if (a === b)
        return null;
    return b ? 'up' : 'down';
}
/** Корзины шага в отчёте; дней нет (старый auto-ads) — `null`. */
export function bucketsOf(report, step) {
    if (step === 'day')
        return report.periods.days ?? null;
    return step === 'month' ? report.periods.months : report.periods.weeks;
}
/** Хоть в одном из трёх отчётов нет корзин шага (дни у старого auto-ads). */
export function bucketsMissing(reports, step) {
    return STAGES.some((s) => bucketsOf(reports[s], step) === null);
}
/**
 * Корзины шага трёх отчётов, сведённые по началу (по порядку). Покупки,
 * расход и факт от момента не зависят — берутся из отчёта 14-го дня, если
 * корзина в нём есть, иначе «через сутки», иначе «при покупке».
 */
export function momentWeeks(reports, h, theta, step = 'week') {
    const byFrom = new Map();
    for (const s of STAGES) {
        for (const row of bucketsOf(reports[s], step) ?? []) {
            const e = byFrom.get(row.from) ?? {};
            e[s] = row;
            byFrom.set(row.from, e);
        }
    }
    return [...byFrom.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([from, rows]) => {
        const base = (rows.d14 ?? rows.t24 ?? rows.t0);
        const stages = {
            t0: rows.t0 ? stagePoint(rows.t0, 't0', h) : null,
            t24: rows.t24 ? stagePoint(rows.t24, 't24', h) : null,
            d14: rows.d14 ? stagePoint(rows.d14, 'd14', h) : null,
        };
        const few = base.group.purchases < FEW_PURCHASES;
        const value = (s) => (stages[s]?.ready ? (stages[s]?.roas ?? null) : null);
        const cross1 = crossOf(stages.t0, stages.t24, theta);
        const cross2 = crossOf(stages.t24?.ready ? stages.t24 : stages.t0, stages.d14, theta);
        const fact = factPoint(base, h);
        return {
            from,
            to: base.to,
            label: rowLabel(base, step),
            short: shortRowLabel(base, step),
            partial: base.partial,
            purchases: base.group.purchases,
            spendMinor: base.group.spendMinorFull,
            few,
            stages,
            fact,
            shift1: relDelta(value('t24'), value('t0')),
            shift2: relDelta(value('d14'), value('t0')),
            cross1,
            cross2,
            crossed: cross1 !== null || cross2 !== null,
            factErr: fact.state === 'none' ? null : relDelta(fact.roas, stages.d14?.predMatured),
        };
    });
}
/** Все три этапа посчитаны и дали число. */
export function hasAllStages(w) {
    return STAGES.every((s) => w.stages[s]?.ready === true && ok(w.stages[s]?.roas));
}
/** Последний посчитанный этап недели (для полосы и шанса). */
export function lastReadyStage(w) {
    for (const s of [...STAGES].reverse()) {
        const p = w.stages[s];
        if (p?.ready && ok(p.roas))
            return p;
    }
    return null;
}
export function sortLadder(weeks, sort) {
    const out = [...weeks];
    if (sort === 'week')
        return out;
    const val = (w) => (sort === 'n' ? w.purchases : w.shift2);
    // Без значения — в конце; при равенстве — по неделе.
    return out.sort((a, b) => {
        const x = val(a);
        const y = val(b);
        if (x === null && y === null)
            return 0;
        if (x === null)
            return 1;
        if (y === null)
            return -1;
        return y - x;
    });
}
/**
 * Среднее, взвешенное по `n`. Для «вернётся на 1 $» вес — расход недели: тогда
 * Σ(roas × расход) / Σрасхода = Σвыручки / Σрасхода, как в «Итого».
 */
export function weighted(items, n, v) {
    let s = 0;
    let w = 0;
    for (const x of items) {
        s += v(x) * n(x);
        w += n(x);
    }
    return w > 0 ? s / w : NaN;
}
/**
 * Итог: «вернётся на 1 $» каждого этапа за весь период = Σвыручки / Σрасхода,
 * то есть среднее, взвешенное по РАСХОДУ, по корзинам, где есть все три этапа
 * — в том числе с малым числом покупок (их вес по расходу и так мал). Факт и
 * ошибка — тоже по расходу созревших корзин итога.
 */
export function ladderTotal(weeks) {
    const set = weeks.filter((w) => hasAllStages(w) && w.spendMinor > 0);
    if (set.length === 0)
        return null;
    const n = (w) => w.spendMinor;
    const avg = Object.fromEntries(STAGES.map((s) => [s, weighted(set, n, (w) => w.stages[s].roas)]));
    const matured = set.filter((w) => w.fact.state === 'mature' && ok(w.stages.d14?.predMatured));
    const pending = set.filter((w) => w.fact.state !== 'mature').map((w) => w.fact.expectedFrom);
    const fact = matured.length > 0 ? weighted(matured, n, (w) => w.fact.roas) : null;
    const factPred = matured.length > 0 ? weighted(matured, n, (w) => w.stages.d14.predMatured) : null;
    return {
        weeks: set.length,
        few: set.filter((w) => w.few).length,
        purchases: set.reduce((a, w) => a + w.purchases, 0),
        spendMinor: set.reduce((a, w) => a + w.spendMinor, 0),
        avg,
        shift1: avg.t24 / avg.t0 - 1,
        shift2: avg.d14 / avg.t0 - 1,
        factErr: fact !== null && factPred !== null ? fact / factPred - 1 : null,
        fact,
        factPred,
        factWeeks: matured.length,
        factExpectedFrom: matured.length > 0 || pending.length === 0 ? null : pending.sort()[0],
    };
}
/**
 * Ошибка каждого этапа против факта: корзины с фактом (созревшие и частично),
 * итог — по созревшим (≥ 90 %) корзинам (и с малым числом покупок), где все три этапа готовы
 * (`ready`: прогноз этапа есть у достаточной доли покупок — то же правило, что
 * у «Лесенки») и у каждого есть прогноз по созревшим покупкам. Итог взвешен по
 * расходу недель.
 */
export function accuracyOf(weeks) {
    const rows = weeks
        .filter((w) => w.fact.state !== 'none')
        .map((w) => {
        const pred = Object.fromEntries(STAGES.map((s) => [s, w.stages[s]?.predMatured ?? null]));
        const err = Object.fromEntries(STAGES.map((s) => [s, relDelta(w.fact.roas, pred[s])]));
        return {
            week: w,
            pred,
            err,
            inTotal: w.fact.state === 'mature' &&
                w.spendMinor > 0 &&
                STAGES.every((s) => w.stages[s]?.ready === true && ok(pred[s])),
        };
    });
    const set = rows.filter((r) => r.inTotal);
    if (set.length === 0)
        return { rows, total: null };
    const n = (r) => r.week.spendMinor;
    const fact = weighted(set, n, (r) => r.week.fact.roas);
    const pred = Object.fromEntries(STAGES.map((s) => [s, weighted(set, n, (r) => r.pred[s])]));
    return {
        rows,
        total: {
            weeks: set.length,
            few: set.filter((r) => r.week.few).length,
            purchases: set.reduce((a, r) => a + r.week.purchases, 0),
            spendMinor: set.reduce((a, r) => a + n(r), 0),
            pred,
            fact,
            stages: STAGES.map((s) => {
                const es = set.map((r) => r.err[s]);
                return { stage: s, err: fact / pred[s] - 1, min: Math.min(...es), max: Math.max(...es) };
            }),
        },
    };
}
/**
 * Срок доступен: есть хоть одна неделя с созревшим фактом (`fact-maturity`,
 * ≥ 90 %). Правило общее с «Сбывается ли прогноз» (`horizonHasMatureFact`).
 */
export function hasMatureFact(weeks) {
    return horizonHasMatureFact(weeks);
}
/**
 * Когда появится первый созревший факт за H дней: самая ранняя неделя без
 * созревшего факта, её последний день + H + 3. Нет недель — `null`.
 */
export function firstFactFrom(weeks) {
    return firstPendingFactFrom(weeks);
}
/**
 * Следующая корзина, у которой созреет факт: дата созревания ещё впереди и
 * покупок хватает для факта (≥ 5) — иначе «следующий — 15 авг, ≈ 17 окт»
 * указывал бы в прошлое или на корзину, где факта не будет.
 */
export function nextMaturingWeek(weeks, today) {
    return (weeks.find((w) => w.purchases >= FACT_MIN_N && w.fact.state !== 'mature' && w.fact.expectedFrom > today) ?? null);
}
// --- слова -----------------------------------------------------------------------
const NNBSP = '\u202F';
const MINUS = '−';
/** «+11 %», «−3 %», «0 %». */
export function signedPct(d) {
    if (!ok(d))
        return '—';
    const r = Math.round(d * 100);
    if (r === 0)
        return `0${NNBSP}%`;
    return `${r > 0 ? '+' : MINUS}${Math.abs(r)}${NNBSP}%`;
}
/** Тон дельты: меньше 2 % — нейтральная. */
export function deltaTone(d) {
    if (!ok(d) || Math.abs(d) < 0.02)
        return 'flat';
    return d > 0 ? 'up' : 'down';
}
/** «≈ 25 окт». */
export const approxDay = (ymd) => `≈${NNBSP}${dayLabel(ymd)}`;
const PURCHASE_FORMS = ['покупка', 'покупки', 'покупок'];
function crossParts(w, sw) {
    const parts = [];
    const up = v(sw, 'поднялась', 'поднялся');
    if (w.cross1) {
        parts.push(w.cross1 === 'up'
            ? `${up} выше порога через сутки`
            : `${v(sw, 'ушла', 'ушёл')} ниже порога через сутки`);
    }
    if (w.cross2) {
        parts.push(w.cross2 === 'up'
            ? `${w.cross1 === 'down' ? v(sw, 'вернулась', 'вернулся') : up} выше порога на 14-й день`
            : `${v(sw, 'опустилась', 'опустился')} ниже порога на 14-й день`);
    }
    return parts;
}
/** «21–27 сен ушла ниже порога через сутки, вернулась выше порога на 14-й день». */
export function crossText(w, step = 'week') {
    const p = crossParts(w, stepWords(step));
    return p.length ? `${w.label} ${p.join(', ')}` : null;
}
/** «На этом шаге неделя поднялась выше порога» — для подсказки клетки «Карты». */
export function crossStepText(cross, step = 'week') {
    const sw = stepWords(step);
    if (cross === 'up')
        return ` На этом шаге ${sw.one} ${v(sw, 'поднялась', 'поднялся')} выше порога.`;
    if (cross === 'down') {
        return ` На этом шаге ${sw.one} ${v(sw, 'опустилась', 'опустился')} ниже порога.`;
    }
    return '';
}
/** Диапазон ошибок словами: «на 11–19 % ниже», «на 3 % выше», «от −4 до +6 %». */
export function errRangeText(errs) {
    const lo = Math.min(...errs);
    const hi = Math.max(...errs);
    const a = Math.round(Math.abs(lo) * 100);
    const b = Math.round(Math.abs(hi) * 100);
    if (hi < 0) {
        const x = Math.min(a, b);
        const y = Math.max(a, b);
        return `на ${x === y ? x : `${x}–${y}`}${NNBSP}% ниже`;
    }
    if (lo > 0) {
        const x = Math.min(a, b);
        const y = Math.max(a, b);
        return `на ${x === y ? x : `${x}–${y}`}${NNBSP}% выше`;
    }
    return `от ${signedPct(lo)} до ${signedPct(hi)}`.replace(`${NNBSP}% до`, ' до');
}
/**
 * Созревший факт для предупреждения: выбранный срок, а если у него факта ещё
 * нет — самый длинный срок, где он есть. Все корзины, и с малым числом покупок.
 */
export function factCheckOf(byHorizon, h) {
    const order = [h, ...[...FORECAST_HORIZONS].reverse().filter((x) => x !== h)];
    for (const x of order) {
        const weeks = byHorizon[x].filter((w) => w.fact.state === 'mature' && ok(w.factErr));
        if (weeks.length)
            return { h: x, weeks, errs: weeks.map((w) => w.factErr) };
    }
    return null;
}
/**
 * Как сравниваются факт и прогноз — одна фраза для строки-вывода и сносок
 * «Лесенки» и «Карты»: шкала «вернётся на 1 $» там после вычетов, а это
 * сравнение — без них.
 */
export const FACT_COMPARE_NOTE = 'Сравнение — по созревшим покупкам, у которых есть прогноз этого этапа; расхождение с фактом — прогноз и факт без вычетов.';
/**
 * Почему число факта нельзя ставить рядом с прогнозами клеток: факт приходит
 * только без вычетов, а прогнозы и порог — после них.
 */
export const FACT_NO_FEES_NOTE = 'Факт известен только без вычетов возвратов, споров и комиссий, поэтому его нельзя сравнивать с прогнозами рядом и с порогом — они после вычетов.';
/**
 * Сколько переходов порога перечислять в строке-выводе: на «Днях» их бывают
 * десятки — дальше «и ещё N дней» (все они — фильтром «только перешедшие
 * порог» «Лесенки» и жёлтыми уголками «Карты»).
 */
export const CROSS_LIST_MAX = 5;
/** Строка-вывод «Лесенки» — целиком из данных. */
export function ladderSummary(weeks, total, check, step = 'week') {
    const sw = stepWords(step);
    const crossed = weeks.filter((w) => w.crossed);
    let lead = null;
    let mid = null;
    if (total) {
        const d2 = total.shift2;
        const d1 = total.shift1;
        const r2 = Math.round(Math.abs(d2) * 100);
        const verb = r2 === 0
            ? 'почти не изменился'
            : d2 > 0
                ? `вырос на ${r2}${NNBSP}%`
                : `снизился на ${r2}${NNBSP}%`;
        lead = `К 14-му дню прогноз в среднем ${verb}: ${num2(total.avg.t0)} → ${num2(total.avg.t24)} → ${num2(total.avg.d14)} $.`;
        const step2 = total.avg.d14 / total.avg.t24 - 1;
        const first = Math.abs(d1) < 0.02
            ? `Через сутки почти ничего не меняется (${signedPct(d1)})`
            : `Через сутки прогноз сдвигается на ${signedPct(d1)}`;
        mid =
            Math.abs(step2) > Math.abs(d1)
                ? `${first}, главный сдвиг даёт первое продление.`
                : `${first}, первое продление сдвигает его меньше (${signedPct(step2)}).`;
    }
    const shown = crossed.slice(0, CROSS_LIST_MAX).map((w) => crossText(w, step));
    const rest = crossed.length - shown.length;
    const cross = crossed.length
        ? `Порог ${vn(sw, crossed.length, 'переходила', 'переходил', 'переходили')} ${countWord(sw, crossed.length)}: ${shown.join('; ')}${rest > 0 ? ` и ещё ${countWord(sw, rest)}` : ''}.`
        : `Ни ${v(sw, 'одна', 'один')} ${sw.one} не ${v(sw, 'перешла', 'перешёл')} порог между этапами.`;
    let warn = null;
    if (check) {
        const allBelow = check.errs.every((e) => e < 0);
        const tail = allBelow && total && total.shift2 > 0 ? ' — рост к 14-му дню пока не подтверждён.' : '.';
        warn = `${allBelow ? 'Осторожно: т' : 'Т'}ам, где факт за ${check.h} дней уже есть (${countWord(sw, check.weeks.length)}), реальность оказалась ${errRangeText(check.errs)} прогноза 14-го дня${tail} ${FACT_COMPARE_NOTE}`;
    }
    return { lead, mid, cross, warn, crossedCount: crossed.length };
}
/** Строка-вывод «Карты»: сколько корзин выше порога на каждом сроке. */
export function mapSummary(groups, theta, step = 'week') {
    const sw = stepWords(step);
    const text = groups
        .map(({ h, weeks }) => {
        const seen = weeks.filter((w) => lastReadyStage(w) !== null);
        const above = seen.filter((w) => (lastReadyStage(w)?.roas ?? 0) >= theta).length;
        return `за ${h} дней выше порога ${above} из ${countOf(sw, seen.length)}`;
    })
        .join('; ');
    return `По последнему прогнозу ${text}.`;
}
/** Ошибки этапов «одинаковы», если отличаются меньше чем на 1 п.п. */
export const ACCURACY_TIE = 0.01;
/** Кто ошибался меньше и больше всех; ничьи (< 1 п.п.) — вместе. */
export function accuracyRanking(stages) {
    const sorted = [...stages].sort((x, y) => Math.abs(x.err) - Math.abs(y.err));
    const lo = Math.abs(sorted[0].err);
    const hi = Math.abs(sorted.at(-1).err);
    if (hi - lo < ACCURACY_TIE)
        return { tie: true, best: sorted, worst: [], others: [] };
    const best = sorted.filter((x) => Math.abs(x.err) - lo < ACCURACY_TIE);
    const worst = sorted.filter((x) => hi - Math.abs(x.err) < ACCURACY_TIE && !best.includes(x));
    const others = sorted.filter((x) => !best.includes(x) && !worst.includes(x));
    return { tie: false, best, worst, others };
}
/** Созревший факт за срок есть, но итог «Точности» не считается. */
export function accuracyNoTotalText(a, h, step = 'week') {
    const sw = stepWords(step);
    const mature = a.rows.filter((r) => r.week.fact.state === 'mature').length;
    if (mature === 0)
        return `Созревшего факта за ${h} дней ещё нет.`;
    return `Факт за ${h} дней есть (${countWord(sw, mature)}), но для итога ${v(sw, 'ни одна', 'ни один')} из них не подходит: нужны прогнозы всех трёх этапов.`;
}
/** Выжимка «Точности» для чтения с экрана. */
export function accuracyAria(a, h, step = 'week') {
    if (!a.total)
        return accuracyNoTotalText(a, h, step);
    return `Ошибка против факта за ${h} дней (${stepWords(step).genPl} с фактом: ${a.total.weeks}): ${a.total.stages
        .map((s) => `${STAGE_LABEL[s.stage]} ${signedPct(s.err)}`)
        .join(', ')}.`;
}
/** «Вывод опирается на 3 недели (1 845 покупок)» — винительный по шагу. */
export function basisText(n, purchases, step = 'week') {
    return `${countAcc(stepWords(step), n)} (${intRu(purchases)} ${pluralRu(purchases, PURCHASE_FORMS)})`;
}
// --- подсказка корзины «Лесенки» ---------------------------------------------------
/**
 * Факт против прогноза 14-го дня по тем же покупкам — с обоими числами (оба
 * без вычетов) и процентом: «вернулось 0,76 $ без вычетов; прогноз 14-го дня
 * по тем же покупкам — 0,92 $ тоже без вычетов: факт на 17 % ниже (−17 %)».
 */
function factVsPred(roas, pred, err) {
    if (!ok(err) || !ok(pred))
        return `вернулось ${num2(roas)} $ без вычетов`;
    const r = Math.round(Math.abs(err) * 100);
    const how = r === 0 ? 'совпал с ним' : `на ${r}${NNBSP}% ${err < 0 ? 'ниже' : 'выше'} (${signedPct(err)})`;
    return `вернулось ${num2(roas)} $ без вычетов; прогноз 14-го дня по тем же покупкам — ${num2(pred)} $ тоже без вычетов: факт ${how}`;
}
const interval = (p) => ok(p.lo) && ok(p.hi) ? ` (скорее всего от ${num2(p.lo)} до ${num2(p.hi)})` : '';
function notYetLine(p, today) {
    const rest = p.readyFrom > today
        ? `у остальных появится ${approxDay(p.readyFrom)}`
        : 'у остальных его пока нет';
    return `Прогноз «${STAGE_LABEL[p.stage]}» пока есть у ${p.withPrediction} из ${p.purchases} покупок, ${rest}.`;
}
/** Факт недели словами; `shorter` — тот же факт за более короткий срок, если за H его ещё нет. */
export function factLine(w, h, today, shorter) {
    const f = w.fact;
    if (factTooFew(w, today))
        return thinLine(w, h);
    if (f.state === 'none') {
        const when = f.expectedFrom > today ? ` — появится ${approxDay(f.expectedFrom)}` : '';
        const sf = shorter?.week;
        const extra = sf && sf.fact.state !== 'none' && ok(sf.fact.roas) && ok(sf.factErr)
            ? ` За ${shorter.h} дней на деле ${factVsPred(sf.fact.roas, sf.stages.d14?.predMatured ?? null, sf.factErr)}.`
            : '';
        return `Факта за ${h} дней ещё нет${when}.${extra}`;
    }
    const vs = factVsPred(f.roas, w.stages.d14?.predMatured ?? null, w.factErr);
    const part = f.state === 'partial'
        ? ` Срок прожили только ${pct(f.share)} покупок — цифра ещё сдвинется.`
        : '';
    return `За ${h} дней на деле ${vs}.${part}`;
}
/**
 * Факта по корзине не будет (`factTooFew`): число по < 5 покупкам (если
 * пришло) — честно, но бледно и без сравнения с прогнозом; иначе — «мало
 * покупок для факта», а не «ещё нет» (как будто он придёт).
 */
function thinLine(w, h) {
    const f = w.fact;
    if (f.thinRoas === null) {
        return `Мало покупок для факта за ${h} дней: нужно хотя бы ${FACT_MIN_N}, а здесь ${intRu(w.purchases)}.`;
    }
    return `За ${h} дней на деле вернулось ${num2(f.thinRoas)} $ без вычетов, но это всего ${intRu(f.n)} ${pluralRu(f.n, PURCHASE_FORMS)} — меньше ${FACT_MIN_N}, поэтому факт показан бледно и с прогнозом не сравнивается.`;
}
/** Строка про малое число покупок — общая для подсказок «Лесенки» и «Карты». */
export function fewLine() {
    return `Покупок меньше ${FEW_PURCHASES} — числа показаны полностью, но сдвиги здесь во многом случайны.`;
}
/**
 * Подсказка клетки факта («Карта», «Лесенка»): сколько на деле вернулось на
 * 1 $ за H дней и насколько это отличается от прогноза 14-го дня по тем же
 * покупкам. Оба числа без вычетов — это сказано прямо.
 */
export function factTip(w, h, today) {
    const f = w.fact;
    if (f.state === 'none') {
        const line = factLine(w, h, today);
        const tail = factTooFew(w, today) && f.thinRoas !== null ? ` ${FACT_NO_FEES_NOTE}` : '';
        return `${w.label}: ${line.charAt(0).toLowerCase()}${line.slice(1)}${tail}`;
    }
    const pred = w.stages.d14?.predMatured ?? null;
    const parts = [
        `${w.label}, факт за ${h} дней: на деле вернулось ${num2(f.roas)} $ на каждый потраченный 1 $.`,
    ];
    if (ok(w.factErr) && ok(pred)) {
        const r = Math.round(Math.abs(w.factErr) * 100);
        parts.push(r === 0
            ? `Прогноз 14-го дня по тем же покупкам — ${num2(pred)} $: факт совпал с ним.`
            : `Прогноз 14-го дня по тем же покупкам — ${num2(pred)} $: факт на ${r}${NNBSP}% ${w.factErr < 0 ? 'ниже' : 'выше'} (${signedPct(w.factErr)}).`);
    }
    else {
        parts.push('Прогноза 14-го дня по тем же покупкам нет — сравнить не с чем.');
    }
    parts.push(FACT_NO_FEES_NOTE);
    if (f.state === 'partial') {
        parts.push(`Срок прожили только ${pct(f.share)} покупок — цифра ещё сдвинется.`);
    }
    if (w.few)
        parts.push(fewLine());
    return parts.join(' ');
}
/** Подсказка строки «Лесенки» простым языком — по строке на мысль. */
export function ladderTip(w, h, today, shorter, step = 'week') {
    const go = `Нажмите — кампании ${stepWords(step).thisGen}.`;
    const lines = [`${w.label} · ${intRu(w.purchases)} ${pluralRu(w.purchases, PURCHASE_FORMS)}`];
    if (w.few)
        lines.push(fewLine());
    const { t0, t24, d14 } = w.stages;
    if (t0?.ready && ok(t0.roas)) {
        lines.push(`При покупке ждали ${num2(t0.roas)} $ на каждый потраченный 1 $ после вычетов${interval(t0)}.`);
    }
    else if (t0)
        lines.push(notYetLine(t0, today));
    if (t24?.ready && ok(t24.roas)) {
        const flat = w.shift1 !== null && Math.abs(w.shift1) < 0.02 ? ', почти без изменений' : '';
        lines.push(`Через сутки — ${num2(t24.roas)} $${flat}.`);
    }
    else if (t24)
        lines.push(notYetLine(t24, today));
    if (d14?.ready && ok(d14.roas)) {
        lines.push(`На 14-й день, когда стало известно первое продление, — ${num2(d14.roas)} $ после вычетов${interval(d14)}.`);
    }
    else if (d14)
        lines.push(notYetLine(d14, today));
    const last = lastReadyStage(w);
    const p0 = t0?.ready ? t0.p : null;
    if (ok(p0) && last && last.stage !== 't0' && ok(last.p)) {
        const a = Math.round(p0 * 100);
        const b = Math.round(last.p * 100);
        lines.push(a === b
            ? `Шанс окупиться не изменился: ${pct(last.p)}.`
            : `Шанс окупиться ${b > a ? 'вырос' : 'снизился'} с ${pct(p0)} до ${pct(last.p)}.`);
    }
    else if (last && ok(last.p)) {
        lines.push(`Шанс окупиться — ${pct(last.p)}.`);
    }
    lines.push(factLine(w, h, today, shorter));
    lines.push(go);
    return lines;
}
// --- сортировка «Карты» и «Точности» -------------------------------------------
/** По умолчанию обе таблицы — по дате корзины, от старой к новой (как пришли). */
export const DEFAULT_MOMENTS_TABLE_SORT = { key: 'period', dir: 'asc' };
export const MAP_SORT_KEYS = [
    'period',
    'purchases',
    'shift',
    ...FORECAST_HORIZONS.flatMap((h) => [...STAGES, 'fact'].map((s) => `${h}-${s}`)),
];
/** Прогноз этапа, как он нарисован в клетке: «ещё рано» и «—» — без значения. */
export function stageValue(w, s) {
    const p = w.stages[s];
    return p?.ready ? p.roas : null;
}
/**
 * Строки «Карты» по колонке. Строка — корзина выбранного срока `main`, клетки
 * других сроков ищутся по началу корзины (не по номеру строки: после
 * сортировки номера разъезжаются). Пустые клетки — в конце в обе стороны.
 */
export function sortMapRows(main, byHorizon, sort) {
    const index = new Map(FORECAST_HORIZONS.map((h) => [h, new Map((byHorizon[h] ?? []).map((w) => [w.from, w]))]));
    return sortBy(main, sort, (w, key) => {
        if (key === 'period')
            return w.from;
        if (key === 'purchases')
            return w.purchases;
        if (key === 'shift')
            return w.shift2;
        const [h, s] = key.split('-');
        const gw = index.get(h)?.get(w.from);
        if (!gw)
            return null;
        if (s === 'fact')
            return gw.fact.state === 'none' ? null : gw.fact.roas;
        return stageValue(gw, s);
    });
}
export const ACCURACY_SORT_KEYS = [
    'period',
    'purchases',
    ...STAGES.map((s) => `pred-${s}`),
    'fact',
    ...STAGES.map((s) => `err-${s}`),
];
/** Строки таблицы «Точности» по колонке; итог «Все созревшие» не сортируется. */
export function sortAccuracyRows(rows, sort) {
    return sortBy(rows, sort, (r, key) => {
        if (key === 'period')
            return r.week.from;
        if (key === 'purchases')
            return r.week.purchases;
        if (key === 'fact')
            return r.week.fact.roas;
        const [kind, s] = key.split('-');
        return kind === 'pred' ? r.pred[s] : r.err[s];
    });
}
//# sourceMappingURL=moments-logic.js.map